/**
 * Settings BYOK - Bring Your Own Key Management
 *
 * Per-profile API key management for third-party LLM providers.
 * Keys are stored in OS keychain via safeStorage. The renderer
 * only sees key hints (last 4 chars), never actual keys.
 *
 * @module @humanizer/studio/components/settings/SettingsByok
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../../contexts/ToastContext';
import type { ByokProviderRef, ByokProvider } from '../../types/electron';

// ═══════════════════════════════════════════════════════════════════════════
// CONSTANTS
// ═══════════════════════════════════════════════════════════════════════════

const PROVIDER_INFO: Record<string, { name: string; placeholder: string; privacyUrl: string }> = {
  openai: {
    name: 'OpenAI',
    placeholder: 'sk-...',
    privacyUrl: 'https://openai.com/privacy',
  },
  anthropic: {
    name: 'Anthropic',
    placeholder: 'sk-ant-...',
    privacyUrl: 'https://www.anthropic.com/privacy',
  },
  voyage: {
    name: 'Voyage AI',
    placeholder: 'pa-...',
    privacyUrl: 'https://www.voyageai.com/privacy',
  },
  google: {
    name: 'Google (Gemini)',
    placeholder: 'AIza...',
    privacyUrl: 'https://policies.google.com/privacy',
  },
  cohere: {
    name: 'Cohere',
    placeholder: '...',
    privacyUrl: 'https://cohere.com/privacy',
  },
  gptzero: {
    name: 'GPTZero',
    placeholder: '...',
    privacyUrl: 'https://gptzero.me/privacy',
  },
  'ollama-cloud': {
    name: 'Ollama Cloud',
    placeholder: 'Enter API key from ollama.com/settings/keys',
    privacyUrl: 'https://ollama.com/privacy',
  },
};

// ═══════════════════════════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════════════════════════

export function SettingsByok() {
  const navigate = useNavigate();
  const { addToast } = useToast();
  const [providers, setProviders] = useState<ByokProviderRef[]>([]);
  const [supportedProviders, setSupportedProviders] = useState<ByokProvider[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Add key form state
  const [addingProvider, setAddingProvider] = useState<string | null>(null);
  const [newKey, setNewKey] = useState('');
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ valid: boolean; error?: string; models?: string[] } | null>(null);

  // ─────────────────────────────────────────────────────────────────────────
  // DATA LOADING
  // ─────────────────────────────────────────────────────────────────────────

  const loadProviders = useCallback(async () => {
    if (!window.electronByok) {
      setLoading(false);
      return;
    }

    try {
      const [configured, supported] = await Promise.all([
        window.electronByok.list(),
        window.electronByok.getSupportedProviders(),
      ]);
      setProviders(configured);
      setSupportedProviders(supported);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load providers');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadProviders();
  }, [loadProviders]);

  // ─────────────────────────────────────────────────────────────────────────
  // KEY MANAGEMENT
  // ─────────────────────────────────────────────────────────────────────────

  const handleTestKey = async () => {
    if (!addingProvider || !newKey || !window.electronByok) return;

    setTesting(true);
    setTestResult(null);

    try {
      const result = await window.electronByok.testKey(addingProvider, newKey);
      setTestResult(result);
    } catch (err) {
      setTestResult({
        valid: false,
        error: err instanceof Error ? err.message : 'Test failed',
      });
    } finally {
      setTesting(false);
    }
  };

  const handleSaveKey = async () => {
    if (!addingProvider || !newKey || !window.electronByok) return;

    setError(null);
    setSuccess(null);

    try {
      const result = await window.electronByok.setKey(addingProvider, newKey);
      if (result.success) {
        const providerName = PROVIDER_INFO[addingProvider]?.name || addingProvider;
        setSuccess(`${providerName} key saved successfully`);
        setAddingProvider(null);
        setNewKey('');
        setTestResult(null);
        await loadProviders();
        window.dispatchEvent(new Event('byok-keys-changed'));
        setTimeout(() => setSuccess(null), 3000);
        addToast(
          `${providerName} key saved. Configure which operations use it in Model Settings.`,
          'success',
          {
            duration: 8000,
            action: {
              label: 'Configure Models \u2192',
              onClick: () => navigate('/settings/models'),
            },
          },
        );
      } else {
        setError(result.error || 'Failed to save key');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed');
    }
  };

  const handleRemoveKey = async (provider: string) => {
    if (!window.electronByok) return;

    try {
      const result = await window.electronByok.removeKey(provider);
      if (result.success) {
        setSuccess(`${PROVIDER_INFO[provider]?.name || provider} key removed`);
        await loadProviders();
        window.dispatchEvent(new Event('byok-keys-changed'));
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError(result.error || 'Failed to remove key');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Remove failed');
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="settings-section">
        <div className="settings-loading">
          <span className="settings-loading__spinner" />
        </div>
      </div>
    );
  }

  const configuredProviderIds = new Set(providers.map(p => p.provider));
  const unconfiguredProviders = supportedProviders.filter(p => !configuredProviderIds.has(p));

  return (
    <div className="settings-section">
      <div className="settings-section__header">
        <h2 className="settings-section__title">API Keys (BYOK)</h2>
        <p className="settings-section__description">
          Use your own API keys for third-party LLM providers.
          Keys are stored securely in your OS keychain.
        </p>
      </div>

      <div className="settings-section__content">
        {error && (
          <div className="settings-alert settings-alert--error">{error}</div>
        )}
        {success && (
          <div className="settings-alert settings-alert--success">{success}</div>
        )}

        {/* Configured Providers */}
        {providers.length > 0 && (
          <div className="settings-card">
            <h3 className="settings-card__title">Configured Keys</h3>
            <div className="settings-card__content">
              {providers.map((ref) => {
                const info = PROVIDER_INFO[ref.provider];
                return (
                  <div key={ref.provider} className="settings-byok__provider">
                    <div className="settings-byok__provider-info">
                      <span className="settings-byok__provider-name">
                        {info?.name || ref.provider}
                      </span>
                      <span className="settings-byok__key-hint">
                        ****{ref.keyHint}
                      </span>
                      <span className="settings-byok__added-date">
                        Added {new Date(ref.addedAt).toLocaleDateString()}
                      </span>
                    </div>
                    <button
                      className="btn btn--danger btn--sm"
                      onClick={() => handleRemoveKey(ref.provider)}
                    >
                      Remove
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Add Key */}
        {addingProvider ? (
          <div className="settings-card">
            <h3 className="settings-card__title">
              Add {PROVIDER_INFO[addingProvider]?.name || addingProvider} Key
            </h3>
            <div className="settings-card__content">
              <div className="settings-form__field">
                <label htmlFor="byokKey">API Key</label>
                <input
                  id="byokKey"
                  type="password"
                  value={newKey}
                  onChange={(e) => {
                    setNewKey(e.target.value);
                    setTestResult(null);
                  }}
                  placeholder={PROVIDER_INFO[addingProvider]?.placeholder || 'Enter API key'}
                  autoComplete="off"
                />
              </div>

              {testResult && (
                <div className={`settings-alert ${testResult.valid ? 'settings-alert--success' : 'settings-alert--error'}`}>
                  {testResult.valid
                    ? `Key is valid${testResult.models?.length ? ` (${testResult.models.length} models available)` : ''}`
                    : testResult.error || 'Invalid key'}
                </div>
              )}

              <div className="settings-byok__privacy-warning">
                <p>
                  When using this key, your content will be sent to{' '}
                  {PROVIDER_INFO[addingProvider]?.name || addingProvider}.
                  <a
                    href={PROVIDER_INFO[addingProvider]?.privacyUrl || '#'}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="settings-byok__privacy-link"
                  >
                    {' '}Review their privacy policy
                  </a>
                </p>
              </div>

              <div className="settings-actions">
                <button
                  className="btn btn--secondary"
                  onClick={() => {
                    setAddingProvider(null);
                    setNewKey('');
                    setTestResult(null);
                  }}
                >
                  Cancel
                </button>
                <button
                  className="btn btn--secondary"
                  onClick={handleTestKey}
                  disabled={!newKey || testing}
                >
                  {testing ? 'Testing...' : 'Test Key'}
                </button>
                <button
                  className="btn btn--primary"
                  onClick={handleSaveKey}
                  disabled={!newKey}
                >
                  Save Key
                </button>
              </div>
            </div>
          </div>
        ) : unconfiguredProviders.length > 0 ? (
          <div className="settings-card">
            <h3 className="settings-card__title">Add Provider</h3>
            <div className="settings-card__content">
              <div className="settings-byok__add-grid">
                {unconfiguredProviders.map((provider) => {
                  const info = PROVIDER_INFO[provider];
                  return (
                    <button
                      key={provider}
                      className="btn btn--secondary settings-byok__add-btn"
                      onClick={() => setAddingProvider(provider)}
                    >
                      Add {info?.name || provider}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        ) : null}

        {/* Info Card */}
        <div className="settings-card">
          <h3 className="settings-card__title">About BYOK</h3>
          <div className="settings-card__content">
            <p className="settings-byok__info">
              Bring Your Own Key lets you use your personal API keys from
              providers like OpenAI and Anthropic. Your keys are encrypted and
              stored in your operating system's keychain — they never leave
              this device or pass through any Humanizer servers.
            </p>
            <p className="settings-byok__info">
              When you use a BYOK model, requests go directly from this app
              to the provider. A privacy badge shows where your data goes
              with every response.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
